/**
 * Validation of a drawn subscription region before it is stored or sent
 * to the backend. The backend re-checks everything (and is the
 * enforcement point for old cached bundles and journal replays), this
 * only gives the user immediate feedback instead of a failing sync.
 */

/**
 * Cross product of vectors oa and ob, sign tells on which side of oa the
 * point b lies.
 * @param {number[]} o
 * @param {number[]} a
 * @param {number[]} b
 * @returns {number}
 */
function cross(o, a, b) {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

/**
 * Is q collinear with p-r and between them?
 * @param {number[]} p
 * @param {number[]} q
 * @param {number[]} r
 * @returns {boolean}
 */
function onSegment(p, q, r) {
  return (
    Math.min(p[0], r[0]) <= q[0] && q[0] <= Math.max(p[0], r[0]) &&
    Math.min(p[1], r[1]) <= q[1] && q[1] <= Math.max(p[1], r[1])
  );
}

/**
 * @param {number[]} p1
 * @param {number[]} p2
 * @param {number[]} p3
 * @param {number[]} p4
 * @returns {boolean}
 */
function segmentsIntersect(p1, p2, p3, p4) {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  // Collinear touching counts too, a pinched ring is not simple
  if (d1 === 0 && onSegment(p3, p1, p4)) return true;
  if (d2 === 0 && onSegment(p3, p2, p4)) return true;
  if (d3 === 0 && onSegment(p1, p3, p2)) return true;
  if (d4 === 0 && onSegment(p1, p4, p2)) return true;
  return false;
}

/**
 * @param {{ coordinates?: number[][][] } | null | undefined} geometry - GeoJSON
 *   geometry of the drawn region (Polygon or MultiPolygon, only the first
 *   ring is checked).
 * @returns {{ ok: boolean, reason: string|null }}
 */
export function validateRegion(geometry) {
  if (
    !geometry ||
    !Array.isArray(geometry.coordinates) ||
    geometry.coordinates.length === 0
  ) {
    return { ok: false, reason: 'empty' };
  }

  const ring = geometry.coordinates[0];
  if (!Array.isArray(ring) || ring.length < 4) {
    return { ok: false, reason: 'short' };
  }

  for (const point of ring) {
    if (
      !Array.isArray(point) || point.length < 2 ||
      !Number.isFinite(point[0]) || !Number.isFinite(point[1])
    ) {
      return { ok: false, reason: 'malformed' };
    }
  }

  if (
    ring[0][0] !== ring[ring.length - 1][0] ||
    ring[0][1] !== ring[ring.length - 1][1]
  ) {
    return { ok: false, reason: 'open' };
  }

  // Distinct consecutive points, the ring without its closing point
  const vertices = [];
  for (const point of ring.slice(0, -1)) {
    const last = vertices[vertices.length - 1];
    if (!last || last[0] !== point[0] || last[1] !== point[1]) {
      vertices.push(point);
    }
  }
  if (vertices.length < 3) {
    return { ok: false, reason: 'short' };
  }

  // Self-intersection: every pair of non-adjacent segments. A ring drawn
  // with a crossed stroke (or a vertex dragged across another edge) is
  // not simple and PostGIS would reject or mis-evaluate it.
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const a1 = vertices[i];
    const a2 = vertices[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) {
        continue; // adjacent through the closing point
      }
      if (segmentsIntersect(a1, a2, vertices[j], vertices[(j + 1) % n])) {
        return { ok: false, reason: 'self-intersection' };
      }
    }
  }

  return { ok: true, reason: null };
}
